import { useEffect, useState } from 'react';

const ACCESSIBILITY_SETTINGS_STORAGE = 'accessibility-settings';
const DEFAULT_FONT_SIZE = 16;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 24;

const normalizeFontSize = (fontSize?: number | string | null) => {
  if (fontSize === null || fontSize === undefined) return DEFAULT_FONT_SIZE;
  const parsedValue = typeof fontSize === 'number' ? fontSize : Number(fontSize);

  if (!Number.isFinite(parsedValue)) return DEFAULT_FONT_SIZE;
  if (parsedValue < MIN_FONT_SIZE || parsedValue > MAX_FONT_SIZE) {
    return DEFAULT_FONT_SIZE;
  }

  return parsedValue;
};

export const useAccessibilitySettings = () => {
  const [isHighContrast, setIsHighContrast] = useState(false);

  useEffect(() => {
    const sync = () => {
      try {
        const raw = window.localStorage.getItem(ACCESSIBILITY_SETTINGS_STORAGE);
        const settings = raw ? JSON.parse(raw) : null;
        const contrast = settings?.contrast === 'high' ? 'high' : 'normal';
        const fontSize = normalizeFontSize(settings?.fontSize);
        const fontDelta = fontSize - DEFAULT_FONT_SIZE;

        document.documentElement.dataset.accessibilityContrast = contrast;
        setIsHighContrast(contrast === 'high');

        if (fontDelta === 0) {
          document.documentElement.style.removeProperty('--accessibility-font-delta');
        } else {
          document.documentElement.style.setProperty(
            '--accessibility-font-delta',
            `${fontDelta}px`
          );
        }
      } catch {
        document.documentElement.dataset.accessibilityContrast = 'normal';
        document.documentElement.style.removeProperty('--accessibility-font-delta');
        setIsHighContrast(false);
      }
    };

    sync();
    
    const originalSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function(key: string, value: string) {
      originalSetItem.call(this, key, value);
      if (key === ACCESSIBILITY_SETTINGS_STORAGE) {
        sync();
      }
    };

    const originalRemoveItem = Storage.prototype.removeItem;
    Storage.prototype.removeItem = function(key: string) {
      originalRemoveItem.call(this, key);
      if (key === ACCESSIBILITY_SETTINGS_STORAGE) {
        sync();
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === ACCESSIBILITY_SETTINGS_STORAGE) {
        sync();
      }
    };
    
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', sync);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', sync);
      Storage.prototype.setItem = originalSetItem;
      Storage.prototype.removeItem = originalRemoveItem;
    };
  }, []);

  return { isHighContrast };
};
